import { useEffect, useState } from "react";
import { Button, TextArea } from "../../components/BaseComponents";
import useApi from "../../hooks/useApi";
import { INPUT_FOCUS_RING } from "../../lib/a11yFocus";
import { FORM_INPUT_TEXT_CLASS, FORM_LABEL_CLASS } from "../../lib/formStyles";
import { getCSRFToken } from "../../lib/utils";
import AccordionSection from "../../components/BaseComponents/AccordionSection";

type EmailTemplate = {
    key: string;
    label: string;
    subject: string;
    bodyHtml: string;
    defaultSubject: string;
    defaultBodyHtml: string;
    variables: string[];
    requiredVariables: string[];
    rawVariables: string[];
}

// Validation failures arrive as `errors: string[]` (placeholder rules) or Adonis' `errors: [{ message }]`.
function readErrors(error: any): string[] {
    const errors = error?.body?.errors;
    if (Array.isArray(errors) && errors.length) {
        return errors.map((item: any) => (typeof item === 'string' ? item : item.message));
    }
    return ['Something went wrong. Please try again.'];
}

function EmailTemplateCard({ template }: { template: EmailTemplate }) {
    const api = useApi();
    const isLayout = template.key === 'layout';
    const [subject, setSubject] = useState(template.subject);
    const [bodyHtml, setBodyHtml] = useState(template.bodyHtml);
    const [saved, setSaved] = useState({ subject: template.subject, bodyHtml: template.bodyHtml });
    const [busy, setBusy] = useState(false);
    const [errors, setErrors] = useState<string[]>([]);
    const [message, setMessage] = useState('');
    const [previewHtml, setPreviewHtml] = useState<string | null>(null);

    const run = async (action: () => Promise<void>) => {
        setBusy(true);
        setErrors([]);
        setMessage('');
        try {
            await action();
        } catch (error) {
            setErrors(readErrors(error));
        }
        setBusy(false);
    }

    const draft = async () => ({
        _csrf: await getCSRFToken(),
        subject: isLayout ? undefined : subject,
        bodyHtml,
    });

    const onSave = () => run(async () => {
        await api.put(`admin/emailTemplates/${template.key}`, await draft());
        setSaved({ subject, bodyHtml });
        setMessage('Saved.');
    });

    const onPreview = () => run(async () => {
        const result = await api.post(`admin/emailTemplates/${template.key}/preview`, await draft());
        setPreviewHtml(result.html);
    });

    const onSendTest = () => run(async () => {
        await api.post(`admin/emailTemplates/${template.key}/test`, await draft());
        setMessage('Test email sent to you.');
    });

    const onReset = () => {
        setSubject(template.defaultSubject);
        setBodyHtml(template.defaultBodyHtml);
        setErrors([]);
        setMessage('Default restored. Select Save to keep it.');
    }

    const changed = bodyHtml !== saved.bodyHtml || (!isLayout && subject !== saved.subject);
    const bodyId = `email-template-body-${template.key}`;
    const subjectId = `email-template-subject-${template.key}`;

    return (
        <AccordionSection id={`email-template-${template.key}`} title={template.label}>
            <div className="space-y-3">
                <p className="text-sm">
                    Available variables:{' '}
                    {[...template.variables.map(name => `{{${name}}}`), ...template.rawVariables.map(name => `{{{${name}}}}`)]
                        .map(text => (<code key={text} className="mr-2">{text}</code>))}
                </p>
                {template.requiredVariables.length > 0 && (
                    <p className="text-sm">
                        Required in the body:{' '}
                        {template.requiredVariables.map(name => (<code key={name} className="mr-2">{`{{${name}}}`}</code>))}
                    </p>
                )}
                {!isLayout && (
                    <div>
                        <label htmlFor={subjectId} className={`block ${FORM_LABEL_CLASS} my-1`}>Subject</label>
                        <input
                            id={subjectId}
                            type="text"
                            className={`block w-full rounded shadow-sm ${FORM_INPUT_TEXT_CLASS} ${INPUT_FOCUS_RING} text-charcoal-800`}
                            value={subject}
                            disabled={busy}
                            onChange={event => setSubject(event.target.value)}
                        />
                    </div>
                )}
                <TextArea
                    id={bodyId}
                    label="HTML body"
                    rows={12}
                    className="font-mono text-sm"
                    value={bodyHtml}
                    disabled={busy}
                    onChange={event => setBodyHtml(event.target.value)}
                />
                {errors.length > 0 && (
                    <ul role="alert" className="list-disc pl-5 text-error-200">
                        {errors.map(error => (<li key={error}>{error}</li>))}
                    </ul>
                )}
                {message && (<p role="status">{message}</p>)}
                <div className="flex flex-wrap gap-2">
                    <Button type="Primary" text="Save" size="sm" disabled={busy || !changed} onClick={onSave} />
                    <Button type="Outlined" text="Preview" size="sm" disabled={busy} onClick={onPreview} />
                    <Button type="Outlined" text="Send test to me" size="sm" disabled={busy} onClick={onSendTest} />
                    <Button type="Outlined" text="Reset to default" size="sm" disabled={busy} onClick={onReset} />
                </div>
                {previewHtml !== null && (
                    // An empty sandbox disables scripts, forms and same-origin access in the previewed HTML.
                    <iframe
                        title={`${template.label} preview`}
                        sandbox=""
                        srcDoc={previewHtml}
                        className="h-96 w-full rounded bg-white"
                    />
                )}
            </div>
        </AccordionSection>
    );
}

function AdminEmailTemplates() {
    const api = useApi();
    const [templates, setTemplates] = useState<EmailTemplate[] | null>(null);
    const [failed, setFailed] = useState(false);

    useEffect(() => {
        const load = async () => {
            try {
                const result = await api.get('admin/emailTemplates');
                setTemplates(result.templates);
            } catch (_) {
                setFailed(true);
            }
        }
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    return (
        <div className="mt-8">
            <h3 className="text-white">Emails</h3>
            <p className="mt-2 text-sm text-white">
                Every email Neoni sends can be edited here. Changes apply to the next email sent; earlier versions are kept.
            </p>
            {failed && (<p role="alert" className="mt-4 text-error-200">Email templates could not be loaded.</p>)}
            {!templates && !failed && (<p className="mt-4 text-white">Loading email templates...</p>)}
            {templates?.map(template => (<EmailTemplateCard key={template.key} template={template} />))}
        </div>
    );
}

export default AdminEmailTemplates;
